/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable no-empty */
import { osName, osVersion, browserName, fullBrowserVersion } from "react-device-detect";
import environment from "@menu-environment";
import { ConnectTokenResponse, Settings } from "@menu/core/types";
import { storage, generateGuid } from "@lib-shared/lib";
import { windowProps } from "@menu/core/constants";

const haptikEventName = "haptik_sdk";
const haptikJsUrl = "https://toolassets.haptikapi.com/platform/javascript-xdk/production/loader.js";

class HaptikSdk {
  private _settings: Settings;

  constructor(settings: Settings) {
    this._settings = settings;
  }

  get haptikSdk() {
    return windowProps.HaptikSDK;
  }

  signIn(data: ConnectTokenResponse) {
    //Should only run once. Loads script & then evenlistener gets fired.
    if (windowProps.HaptikSDK) {
      return;
    }

    const settings = this._settings;

    const listener = function () {
      const haptikSdk = windowProps.HaptikSDK;
      const connectUser = data;

      const addListener = connectUser != null || connectUser !== null;

      if (!addListener) {
        return;
      }

      const obj = connectUser;

      for (let i = 0, len = localStorage.length; i < len; ++i) {
        const key: any = localStorage.key(i);
        let tf = false;

        if (key != null || key !== null) {
          tf = key.startsWith("2412");
        }

        if (!tf) {
          continue;
        }

        const item = storage.getItem(key);
        const store = JSON.parse(item);

        if (!("_user" in store)) {
          continue;
        }

        if ("email" in store._user && store._user.email === obj.email) {
          continue;
        }

        localStorage.removeItem(key);
        haptikSdk.logout();
        window.location.reload();
      }

      const customData = {
        country_code: obj.countryCode,
        connect_id: obj.connectId,
        customer_no: obj.CustomerNumber,
        authenticated: obj.authenticated,
        agreement_id: obj.agreementId,
        referrer: settings.haptik.referrer,
        user_device: `${osName} (${osVersion})`,
        user_browser: `${browserName} (${fullBrowserVersion})`,
        user_ip: settings.userIpAddress,
        user_start_url: window.location.href,
        campaign: settings.haptik.campaign
      };

      haptikSdk.signup(
        {
          auth_id: obj.CustomerNumber,
          auth_code: obj.id_token,
          email: obj.email,
          mobile_no: obj.mobileNumber,
          username: "",
          custom_data: customData,
        },
        function () { }
      );
    }

    document.addEventListener(haptikEventName, listener, { once: true });

    this.initializeBotSetting();
  }

  guestSignIn() {
    //Should only run once. Loads script & then evenlistener gets fired.
    if (this.haptikSdk) {
      return;
    }

    const settings = this._settings;

    const listener = () => {
      const haptikSdk = windowProps.HaptikSDK;

      for (let i = 0, len = localStorage.length; i < len; ++i) {
        const key: any = localStorage.key(i);
        let tf = false;

        if (key != null || key !== null) {
          tf = key.startsWith("2412");
        }

        if (!tf) {
          continue;
        }

        const item = storage.getItem(key);
        const store = JSON.parse(item);

        if (!("_user" in store)) {
          continue;
        }

        if ("email" in store._user && store._user.email == null) {
          continue;
        }

        localStorage.removeItem(key);
        haptikSdk.logout();
        window.location.reload();
      }

      const customData = {
        country_code: settings.threeLetterCountryCode,
        authenticated: false,
        referrer: settings.haptik.referrer,
        user_device: `${osName} (${osVersion})`,
        user_browser: `${browserName} (${fullBrowserVersion})`,
        user_ip: settings.userIpAddress,
        user_start_url: window.location.href,
        campaign: settings.haptik.campaign
      };

      haptikSdk.signup(
        {
          auth_id: generateGuid(),
          auth_code: new Date().toISOString(),
          custom_data: customData,
        },
        function () { }
      );
    };

    document.addEventListener(haptikEventName, listener, { once: true });

    this.initializeBotSetting();
  }

  signOut() {
    const haptikSdk = this.haptikSdk;

    if (!haptikSdk) {
      return;
    }

    haptikSdk.logout(function (success: any, error: any) {
      if (success) {
        haptikSdk.destroy();
        setTimeout(() => window.location.reload());
      }

      if (error) {
      }
    });
  }

  launchMessage(message: any, callback: () => void, hidden: any) {
    this.haptikSdk.launchMessage(message, callback(), hidden);
  }

  launchTask(taskId: any, callback: () => void) {
    this.haptikSdk.launchTask(taskId, callback());
  }

  prompt(title: any, message: any) {
    this.haptikSdk.launchTask(title, message);
  }

  initializeBotSetting() {
    if (!window.location.href.includes(environment.menu.defaultCulture)) {
      return;
    }

    if (!environment.production) {
      return;
    }

    const settings = this._settings;

    const haptik = settings.haptik;

    windowProps.haptikInitSettings = {
      "business-id": haptik.bot.businessClientId,
      "client-id": haptik.bot.clientId,
      "base-url": haptik.baseUrl,
      "privacy-policy-url": haptik.privacyPolicyUrl,
      "custom-css": haptik.customCss,
      "blacklisted-params": haptik.blacklistedParams,
      "signup-type": "third_party"
    };

    const blackListed = (haptik.blacklistedParams ?? "").split(",");
    let isBlacklisted = false;

    try {
      const pathName = window.location.pathname.split("/")[1];

      if (blackListed.length) {
        const blackList = blackListed.filter(
          (param: string) => param.toLowerCase() === pathName.toLowerCase()
        );

        if (blackList.length) {
          isBlacklisted = true;
        }
      }
    } catch (error) {
      console.error("there was an error checking the current url..", error);
    }

    if (isBlacklisted) {
      return;
    }

    this.loadHaptikScript();
  }

  loadHaptikScript() {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = haptikJsUrl;

    const head = document.getElementsByTagName("head")[0];
    head.appendChild(script);
  }
};

export default HaptikSdk;
